import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Newspaper,
  RefreshCw,
  Trash2,
  Plus,
  Loader2,
  Upload,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState("");
  const [dataNewsletter, setDataNewsletter] = useState("");
  const [file, setFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/newsletters`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento newsletter");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setNome("");
    setDataNewsletter("");
    setFile(null);
    setDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!nome.trim()) {
      toast.error("Inserisci il nome");
      return;
    }
    if (!file) {
      toast.error("Seleziona un file PDF");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("nome", nome.trim());
      if (dataNewsletter) fd.append("data_newsletter", dataNewsletter);
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/newsletters`, {
        method: "POST",
        headers: authHeaders,
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Upload fallito");
      toast.success("Newsletter caricata");
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Errore upload");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare (disattivare) questa newsletter?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/newsletters/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Eliminazione fallita");
      toast.success("Newsletter eliminata");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (v) => {
    if (!v) return "—";
    try {
      return new Date(v).toLocaleDateString("it-IT");
    } catch {
      return String(v);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-purple-600" />
            Newsletter
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Carica e gestisci le newsletter mostrate alle strutture.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Carica newsletter
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((n) => (
          <Card key={n.id}>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{n.nome || "Senza nome"}</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {formatDate(n.data_newsletter)}
                  {n.file_name ? ` · ${n.file_name}` : ""}
                </p>
                <div className="flex gap-2 mt-2">
                  {n.has_file && <Badge className="bg-red-100 text-red-700">PDF</Badge>}
                  {n.is_active === false && (
                    <Badge variant="secondary">Disattiva</Badge>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={deletingId === n.id}
                onClick={() => handleDelete(n.id)}
              >
                {deletingId === n.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-slate-500">
              Nessuna newsletter
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica newsletter</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={dataNewsletter}
                onChange={(e) => setDataNewsletter(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>File PDF *</Label>
              <Input
                type="file"
                accept=".pdf,application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpload} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
